import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CheckboxOptionModel } from '../../../Forms/models/checkbox-option.model';
import { CheckboxesFieldModel } from '../../../PageBuilder/models/checkboxes-field.model';
import { ReportModel } from '../../models/report.model';
import { combineLatest, filter, Observable, Subscription } from 'rxjs';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { SharedReportModel } from '../../models/shared-report.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { SharedReportCreateDialogComponent } from '../SharedReportCreateDialog/shared-report-create-dialog.component';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ReportsSeriesService } from '../../services/reports-series.service';
import { SeriesStateEnum } from '../../enums/series-state.enum';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { SharedReportSearchService } from '../../services/shared-report-search.service';
import { ReportsService } from '../../services/reports.service';
import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ReportsChartOptionsService } from '../../services/reports-chart-options.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';

enum UpdateChartOptionsEnum {
    titles = 'titles',
    displaySettings = 'displaySettings',
    sourceData = 'sourceData'
}
@Component({
    selector: 'agpr-shared-report-edit-dialog',
    templateUrl: './shared-report-edit-dialog.component.html',
    styleUrls: ['./shared-report-edit-dialog.component.scss'],
    standalone: false
})

export class SharedReportEditDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() sharedReport: SharedReportModel;
    @Input() sourceReport: ReportModel;
    @Input() editSharedReportDetails: AttributeModel[];
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    showWarning: boolean = false;
    subscriptions: Subscription[] = [];
    selectedOptions: UpdateChartOptionsEnum[] = [];
    checkboxFieldModel: CheckboxesFieldModel;
    globalSeriesState: SeriesStateEnum = SeriesStateEnum.none;
    seriesStates = SeriesStateEnum;
    reportError: boolean = false;
    userPreferenceTimezone: string;
    hideSlideOverlay: boolean = false;

    titleOption: CheckboxOptionModel = {
        label: 'Reports.sharedReportEditDialog.content.updateChartTitles',
        key: UpdateChartOptionsEnum.titles,
        tooltip: 'Reports.sharedReportEditDialog.content.updateChartTitles.tooltip'
    };
    displaySettingsOption: CheckboxOptionModel = {
        label: 'Reports.sharedReportEditDialog.content.updateChartDisplaySettings',
        key: UpdateChartOptionsEnum.displaySettings,
        tooltip: 'Reports.sharedReportEditDialog.content.updateChartDisplaySettings.tooltip'
    };
    sourceDataOption: CheckboxOptionModel = {
        label: 'Reports.sharedReportEditDialog.content.updateChartSourceData',
        key: UpdateChartOptionsEnum.sourceData,
    };

    updateOptions: CheckboxOptionModel[] = [
        this.titleOption,
        this.displaySettingsOption,
        this.sourceDataOption
    ];

    constructor(
        coreDialogService: CoreDialogService,
        private reportsSeriesService: ReportsSeriesService,
        private sharedReportSearchService: SharedReportSearchService,
        private reportsService: ReportsService,
        private reportsChartOptionsService: ReportsChartOptionsService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'SharedReportEdit');
        // by default, it is disabled
        this.sourceDataOption.disabled = true;
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.reportsSeriesService.getGlobalState().subscribe(state => {
                this.globalSeriesState = state;
                this.sourceDataOption.disabled = this.globalSeriesState !== SeriesStateEnum.loaded;
            }),
            combineLatest([
                this.reportsService.getCurrentReport(),
                this.reportsSeriesService.getGlobalState()
            ]).subscribe(
                ([report, globalSeries]) => {
                    // Consider the report loaded if the global series is loaded as well
                    // Or for bad data case, users should be able to update title and display settings
                    if (
                        report.id === this.sharedReport.reportId &&
                        (globalSeries === SeriesStateEnum.loaded || globalSeries === SeriesStateEnum.error)
                    ) {
                        this.sourceReport = report;
                    }
                }
            ),
            this.sharedReportSearchService.reportSourceValid$.subscribe(isValid => this.reportError = !isValid),
            this.sharedReportSearchService.getEditSharedReportDetails(this.sharedReport).subscribe(x => this.editSharedReportDetails = x),
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x)).subscribe(
                pref => this.userPreferenceTimezone = pref.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone)
        );

        this.checkboxFieldModel = new CheckboxesFieldModel(
            null,
            null,
            null,
            null,
            this.updateOptions
        );

        this.selectedOptions = [
            UpdateChartOptionsEnum.titles,
            UpdateChartOptionsEnum.displaySettings
        ];
    }

    selectedOptionsChanged(value) {
        this.selectedOptions = value;
        this.showWarning = this.selectedOptions.includes(UpdateChartOptionsEnum.sourceData);
    }

    /**
     * Update shared report based on selected options and open to the create dialog to edit
     */
    updateSharedReport() {
        if (this.selectedOptions.length > 0) {
            // iterate over each SR block and find a matching block of the source report
            for (const chart of this.sharedReport.charts) {
                const chartDisplayElement = chart.displayElement as ChartDisplayElementModel;
                const reportBlock = this.sourceReport.blocks.find(block => block.id === chartDisplayElement.originalReportBlockId);
                if (reportBlock) {
                    const displayElement = reportBlock.displayElement as ChartDisplayElementModel;
                    // update chart titles
                    if (this.selectedOptions.includes(UpdateChartOptionsEnum.titles)) {
                        chartDisplayElement.title = displayElement.title;
                    };
                    // update chart display settings (label display options, color settings)
                    if (this.selectedOptions.includes(UpdateChartOptionsEnum.displaySettings)) {
                        if (chartDisplayElement.styles.chartMetadata) {
                            chartDisplayElement.styles.chartMetadata.labelDisplayOptions = displayElement.labelDisplayOptions;
                        }
                        chartDisplayElement.styles.colorMap = displayElement.styles.colorMap;
                    }
                    // update source data
                    if (this.selectedOptions.includes(UpdateChartOptionsEnum.sourceData)) {
                        const dateRange = displayElement.series[0].dateRange;

                        if (dateRange.type === DateQueryTypeEnum.ALL) {
                            chartDisplayElement.styles.allDateRange = true;
                        } else {
                            chartDisplayElement.styles.absoluteDateRange = DateQueryModel.makeAbsoluteRange(
                                displayElement.series[0].dateRange,
                                this.userPreferenceTimezone
                            );
                        }

                        chartDisplayElement.replaceAllSeries(displayElement.series.map(series => {
                            const newSeries = series.clone();
                            newSeries.dataProvider = series.dataProvider;
                            newSeries.mentionProvider = series.mentionProvider;
                            if (series.subSeries) {
                                series.styles.subSeries = series.subSeries;
                            }
                            return newSeries;
                        }));
                    }
                }
            }
        }
        this.coreDialogService.open(SharedReportCreateDialogComponent, {
            inputs: {
                sharedReport: this.sharedReport,
                charts: this.sharedReport.charts.slice(),
                updateSourceData: this.selectedOptions.indexOf(UpdateChartOptionsEnum.sourceData) !== -1,
                report: this.sourceReport,
                userPreferenceTimezone: this.userPreferenceTimezone
            }
        });

        this.closeDialog();
    }

    cancel() {
        this.closeDialog();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
